import { useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useMemo } from "react";

import {
	activeMemberQueryOptions,
	type PermissionRequest,
	sessionQueryOptions,
} from "@platform/auth/guards";
import { authClient } from "@platform/auth/client";

type Role = "member" | "admin" | "owner";
type SessionUser = NonNullable<Awaited<ReturnType<typeof authClient.getSession>>["data"]>["user"];

interface AuthValue {
	user: SessionUser | null;
	role: Role | null;
	suspended: boolean;
	// UI gating only. The server requirePermission is the real enforcement.
	can: (permission: PermissionRequest) => boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
	const { data: session } = useQuery(sessionQueryOptions);
	const { data: member } = useQuery(activeMemberQueryOptions);

	const value = useMemo<AuthValue>(() => {
		const role = (member?.role as Role | undefined) ?? null;
		// `status` is a custom additionalField not in the client member type.
		const suspended = (member as { status?: string } | null)?.status === "suspended";
		return {
			user: session?.user ?? null,
			role,
			suspended,
			can: (permission) =>
				role
					? authClient.organization.checkRolePermission({
							role,
							permissions: permission,
						} as Parameters<typeof authClient.organization.checkRolePermission>[0])
					: false,
		};
	}, [session, member]);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
	const ctx = useContext(AuthContext);
	if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
	return ctx;
}
